import { Injectable, inject } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { FileUploadService } from "./file-upload.service";
import { MapInteractionService } from "./map-interaction.service";
import { StyleLegendService } from "../../../shared/services/style-legend.service";
import { ToastService } from "../../../viewer-components/services/internal-services/toast.service";
import { TOAST_STATUS } from "../../../viewer-components/model/ToastConstants";
import { FileReaderService } from "../../../viewer-components/services/readers/file-reader.service";
import { VIEWER_SERVICE } from "../../../viewer-components/services/internal-services/viewer-services/inject-base-viewer.service";
import type { BaseViewerService } from "../../../viewer-components/services/internal-services/viewer-services/base-viewer.service";
import { resolveMapLayerContract } from "../model/map-layer.types";
import type { MapLayerContract } from "../model/map-layer.types";
import type { EvidenceLayerUpdateRequest } from "../model/evidence-layer.types";
import type { IStyleLegendMetadata, IVectorStyle } from "../../../shared/model/vector-style-definition.interface";
import type { ContentBlockData } from "../model/block.types";

/** Last link of the S8 naming chain (ActionService add_layer convention). */
export const MAP_LAYER_FALLBACK_NAME = "AI Layer";

/**
 * S8 layer naming chain: `map_layer.name` → the ref filename minus its extension →
 * "AI Layer". NEVER reads `data.label` — that field is card-title/debug only (S8). Empty
 * strings count as absent data at every link, so an empty contract name still falls through.
 */
export function mapLayerDisplayName(name: string | null, filename: string | null | undefined): string {
  if (name) return name;
  const base = filename?.replace(/\.[^.]+$/, "") ?? "";
  return base || MAP_LAYER_FALLBACK_NAME;
}

/**
 * The SINGLE chat-side door for acting on a `map_layer` content block (map-layer-block-ui
 * S3/S4/S5/S8): the map-layer card's one contextual action. One public decision point,
 * `applyFromBlock`, implements the exact S2/S3 update-vs-add rule:
 *
 * - `map_layer.layer_id` present AND resolved to a live layer (`findUserLayerById`, the
 *   `map-frontend-tools.service` chat-reads-map-state precedent) → UPDATE the existing layer
 *   through `MapInteractionService.updateEvidenceLayer` (`update_evidence_layer` event, S5).
 * - `layer_id` present but NOT found on the map → transparent fallback to ADD (user decision:
 *   "add as new") — never an error.
 * - `layer_id` null → ADD as a new layer through `MapInteractionService.addEvidenceLayer`
 *   (the proven manual-add fetch → prepare → emit flow, S4).
 *
 * Both paths fetch `ref.url` and prepare the GeoJSON with `FileReaderService.prepareGeoJson`
 * before emitting; a fetch/prepare failure stops the flow — logged AND notified through the
 * app toast system (S10), never thrown into the caller's stream, never converting an UPDATE
 * into an ADD (the "fetch failure never falls back" house rule from action.service.spec).
 *
 * The inline `map_layer.style`/`style_metadata` are bridged into the
 * `collection.style.{style, metadata}` envelope `AiLayerStyles.attachStyleDefinition` reads
 * on ADD (S4), and ride in the `EvidenceLayerUpdateRequest` on UPDATE, where a successful
 * STYLED update also shows the legend (`StyleLegendService.show`, moved here from the old
 * `apply_layer_style` ack handler — S7). An unstyled block (both keys null) is a normal state:
 * no style key is sent and the map side applies its default styling.
 *
 * Provided in the chat-container providers (NOT root, NOT component-level): the S5 existence
 * probe reads map state through VIEWER_SERVICE, which is provided by the viewer container
 * component tree the chat sits in — the MapFrontendToolsService scoping precedent (that
 * service injects the same token non-optionally from chat-container scope). ActionService is
 * re-scoped in the same providers array and injects this service for the S11 `add_layer`
 * routing. A service-level in-flight guard (S3/S11, workarea-chip precedent) keeps ONE apply
 * at a time — the shared container-scoped instance is safe against double-clicks.
 */
@Injectable()
export class MapLayerBlockService {
  readonly #fileUpload = inject(FileUploadService);

  readonly #fileReader = inject(FileReaderService);

  readonly #map = inject(MapInteractionService);

  readonly #viewer = inject(VIEWER_SERVICE, { optional: true }) as BaseViewerService | null;

  readonly #styleLegend = inject(StyleLegendService);

  readonly #toast = inject(ToastService);

  /** S3/S11: one apply at a time, shared across every card hit through this instance. */
  #applying = false;

  /**
   * S10: user-reachable failures of this flow surface through the app toast system — the
   * existing console logging stays alongside. Success paths stay silent.
   */
  #notifyError(message: string): void {
    this.#toast.setNotification({ message, type: TOAST_STATUS.ERROR });
  }

  /** Named OL style function identity for evidence adds; the embedded definition wins when present. */
  static readonly STYLE_FUNCTION_NAME = "defaultEvidenceStyle";

  /**
   * The card's ONE contextual action (S3) AND the ActionService `add_layer` door for map_layer
   * blocks (S11): resolves the contract, decides UPDATE vs ADD by `layer_id` presence + live
   * existence, then fetches → prepares → emits. Absent contract (defensive: the routing and
   * the card are contract-driven) and every failure are logged, never thrown. The S3/S11
   * in-flight guard makes a call that arrives while another apply is still running a no-op
   * (the workarea-chip guard, service-level so the shared instance is safe); the flag resets
   * in `finally`, so a failed apply never wedges the service.
   */
  async applyFromBlock(block: ContentBlockData): Promise<void> {
    if (this.#applying) {
      console.warn("MapLayerBlockService: an apply is already in flight — ignoring the new request");
      return;
    }
    this.#applying = true;
    try {
      const contract = resolveMapLayerContract(block);
      if (!contract) {
        console.warn("MapLayerBlockService: block carries no data.map_layer contract — nothing to do");
        return;
      }

      // S3/S5 decision: ONLY layer_id participates. Presence selects the candidate branch;
      // the live lookup (or its degradation) selects the actual path.
      const isUpdate = contract.layer_id !== null && this.#layerExists(contract.layer_id);
      if (contract.layer_id !== null && !isUpdate) {
        console.warn(`MapLayerBlockService: layer "${contract.layer_id}" not found on the map — falling back to ADD`);
      }

      try {
        const prepared = await this.#fetchAndPrepare(block);
        if (!prepared) return;

        if (isUpdate) {
          this.#updateExisting(contract, prepared);
        } else {
          this.#addToMap(contract, prepared, mapLayerDisplayName(contract.name, block.ref?.filename));
        }
      } catch (err) {
        console.error("MapLayerBlockService: map-layer action failed", contract.layer_id, err);
        this.#notifyError("Something went wrong applying the layer change");
      }
    } finally {
      this.#applying = false;
    }
  }

  /** S5 existence probe against the live layer tree (get_layer_geometry lineage ids). */
  #layerExists(layerId: string): boolean {
    if (!this.#viewer) {
      // No viewer in scope (e.g. chat without a mounted map): the layer is definitionally
      // not found, so the decision degrades to the ADD fallback like any missing layer.
      console.warn("MapLayerBlockService: no viewer service in scope — layer lookup unavailable, treating the layer as absent");
      return false;
    }
    return this.#viewer.findUserLayerById(layerId) !== undefined;
  }

  /**
   * S4 fetch + prepare, shared by both paths: fetches `ref.url` (never a reconstructed URL),
   * requires the `geojson` result type and a non-empty preparation. Returns the prepared
   * FeatureCollection, or null when the flow must stop (already logged).
   */
  async #fetchAndPrepare(block: ContentBlockData): Promise<any | null> {
    const ref = block.ref;
    if (!ref) {
      console.error("MapLayerBlockService: map-layer block has no file ref — nothing to do");
      return null;
    }

    const result = await (async (): Promise<any> => {
      try {
        return await firstValueFrom(
          this.#fileUpload.fetchFileContent(ref.url, ref.mime_type, {
            filename: ref.filename,
            size_bytes: ref.size_bytes,
          }),
        );
      } catch (err) {
        console.error("MapLayerBlockService: evidence fetch failed", ref.url, err);
        this.#notifyError("Couldn't load the layer data");
        return null;
      }
    })();
    if (!result) return null;
    if (result.type !== "geojson") {
      console.error(`MapLayerBlockService: map-layer evidence arrived as ${result.type}, expected geojson — stopping`);
      this.#notifyError("The evidence file did not arrive as GeoJSON data");
      return null;
    }

    const prepared = this.#fileReader.prepareGeoJson(result.geojson as any, ref.filename);
    if (!prepared.data) {
      console.error("MapLayerBlockService: GeoJSON preparation failed", prepared.errors as unknown);
      this.#notifyError("Couldn't normalize the layer data");
      return null;
    }
    return prepared.data;
  }

  /**
   * ADD path (S4): bridges the inline style into the `collection.style.{style, metadata}`
   * envelope `AiLayerStyles.attachStyleDefinition` reads, then emits `add_evidence_layer`
   * with the named fallback style function identity (an embedded definition wins map-side).
   * Unstyled (both keys null) → NO style key at all, so the map side attaches its default
   * definition fallback — a normal state, never an error.
   */
  #addToMap(contract: MapLayerContract, prepared: any, name: string): void {
    if (contract.style !== null || contract.style_metadata !== null) {
      prepared.style = { style: contract.style, metadata: contract.style_metadata };
    }
    this.#map.addEvidenceLayer(prepared, { name, styleFunctionName: MapLayerBlockService.STYLE_FUNCTION_NAME });
  }

  /**
   * UPDATE path (S5): emits `update_evidence_layer` with the inline style riding in the
   * request (the map side replaces the features, persists and applies the style via
   * `updateLayerStyleDefinition`). On a STYLED update the legend is shown here — the S7
   * receipt moved out of the old `apply_layer_style` handler: the existence check above is
   * the same "layer found" precondition that handler had, and the chat-side emit is
   * fire-and-forget, so this is the equivalent moment in the flow.
   */
  #updateExisting(contract: MapLayerContract, prepared: any): void {
    const layerId = contract.layer_id as string;
    const request: EvidenceLayerUpdateRequest = { layer_id: layerId, collection: prepared };
    // The layer keeps its current name unless the block names it: the evidence file name (a uuid) is not the layer's name.
    if (contract.name) request.name = contract.name;
    // The wire contract types style as an open dict (Record<string, unknown> | null); the
    // IVectorStyle shape is validated map-side by convertVectorStyle (updateLayerStyleDefinition
    // refuses malformed definitions), so the narrowing routes through unknown deliberately.
    const style = contract.style as unknown as IVectorStyle;
    if (contract.style !== null) request.style = style;
    if (contract.style_metadata !== null) request.style_metadata = contract.style_metadata as IStyleLegendMetadata;
    this.#map.updateEvidenceLayer(request);

    if (contract.style !== null) {
      this.#styleLegend.show(
        layerId,
        contract.name ?? this.#viewer?.findUserLayerById(layerId)?.name ?? MAP_LAYER_FALLBACK_NAME,
        style,
        (contract.style_metadata ?? undefined) as IStyleLegendMetadata | undefined,
      );
    }
  }
}
